import { format } from 'date-fns';
import { z } from 'zod';
import type { Period } from '@/entities/types';
import { logError } from '@/services/logger';
import {
  getQuarterStartAt,
  getWeekStartAt,
  getYearStartAt,
  toMidnightLocalString,
} from '@/utils/datetime';

const dateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
  .refine((dateStr) => !isNaN(new Date(dateStr + 'T00:00:00').getTime()), {
    message: 'Invalid date',
  });

interface TargetStartAtResult {
  startAt: Date;
  startAtLocal: string;
}

export function useTargetStartAt(
  period: Period,
  targetDateParam?: string,
): TargetStartAtResult | undefined {
  const today = format(new Date(), 'yyyy-MM-dd');
  const input = targetDateParam || today;
  const result = dateStringSchema.safeParse(input);

  if (!result.success) {
    logError(
      new Error('Invalid date format'),
      'Invalid date format: ' + String(targetDateParam),
    );

    return undefined;
  }

  const dateStr = result.data;
  const targetDayStartAt = new Date(dateStr + 'T00:00:00');

  const periodHandlers = {
    day: (date: Date) => date,
    week: (date: Date) => getWeekStartAt(date),
    quarter: (date: Date) => getQuarterStartAt(date),
    year: (date: Date) => getYearStartAt(date),
  } as const;

  const startAt = periodHandlers[period](targetDayStartAt);

  return {
    startAt,
    startAtLocal: toMidnightLocalString(startAt),
  };
}
